import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { QuotationAttachment, SourcingOutcomeInformation, VendorsList } from 'src/app/shared/models/app-models/SourcingOutcome.model';
import {
  UserInformation,
  ExtendedKeycloakUser
} from 'src/app/shared/models/app-models';
import { lastValueFrom, Observable, Subject, takeUntil } from 'rxjs';
import { BackendApiService } from 'src/app/core/services/backend-api';
import { StatusData } from 'src/app/modules/rfq-summary/rfq-summary.component';
import { SummaryDropdown } from 'src/app/modules/rfq-summary/rfq-summary.component';
import { Store } from '@ngrx/store';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { InputDialogComponent } from 'src/app/shared/components/input-dialog/input-dialog.component';

@Component({
  selector: 'app-quotation-list',
  templateUrl: './quotation-list.component.html',
  styleUrls: ['./quotation-list.component.scss']
})

export class QuotationListComponent {
  @Input() attachments!: QuotationAttachment[];
  @Input() user!: UserInformation;
  @Input() statusData!: StatusData;
  @Input() vendor!: VendorsList;
  @Input() userList!: ExtendedKeycloakUser[];

  @Input() isReadOnlyRFQSummary: boolean = false;
  @Input() rfqAttachments: SummaryDropdown[] = [];
  @Output() quotationUploadChanged: EventEmitter<any> = new EventEmitter();
  rfqSummary$: Observable<SourcingOutcomeInformation>;
  rfqSummaryDetails: SourcingOutcomeInformation = {} as SourcingOutcomeInformation;
  destroy$: Subject<boolean> = new Subject<boolean>();

  dataSource: MatTableDataSource<QuotationAttachment> = new MatTableDataSource<QuotationAttachment>(this.attachments);

  columns = [
    {
      columnDef: 'FileName',
      header: 'File name',
      cell: (element: any) => element.FileName, //this referenced from your processed data.
    },
    {
      columnDef: 'Purpose',
      header: 'Purpose',
      cell: (element: any) => element.Purpose || 'Select Purpose',
    },
    {
      columnDef: 'UploadedBy',
      header: 'Uploaded by',
      cell: (element: any) => element.CreatedBy,
    },
    {
      columnDef: 'Action',
      header: 'Action',
      cell: () => '',
    },
  ];

  displayedColumns = this.columns.map((c) => c.columnDef);

  constructor(private BackendApi: BackendApiService, private store: Store<{
    rfqSummary: SourcingOutcomeInformation
  }>, private dialogRef: MatDialogRef<InputDialogComponent>, private dialog: MatDialog
  ) {
    this.rfqSummary$ = this.store.select('rfqSummary');
    this.rfqSummary$
      .pipe(takeUntil(this.destroy$))
      .subscribe((summaryData: any) => {
        this.rfqSummaryDetails = summaryData as SourcingOutcomeInformation;
      });
  }

  ngOnInit() {
    this.dataSource.data = this.attachments;
    this.displayedColumns = this.columns
      .map(c => c.columnDef)
      .filter(column => !(this.isReadOnlyRFQSummary && column === 'Action'));
    this.checkVendorStatus();
  }

  getCreatedByUser(createdUser: string) {
    return this.userList.find((user) => user.id == createdUser)?.last_name ?? createdUser;
  }

  checkVendorStatus() {
    this.vendor.Status = this.vendor.QuotationAttachment.length == 0 ||
      this.vendor.QuotationAttachment.every(quotation =>
        (quotation.IsDeleted || quotation.IsStrikethrough || (quotation.AttachmentTypeId !== 1 && quotation.AttachmentTypeId !== 6))
      )
      ? false
      : true;
    if (this.vendor.Status && this.vendor.ReasonForNoQuotationId) {
      this.vendor.ReasonForNoQuotationId = null;
    } else if (!this.vendor.Status && this.vendor.QuoteExpiring) {
      this.vendor.QuoteExpiring = null;
    }
  }

  private convertToBase64(file: File): Promise<any> {
    const reader = new FileReader();
    const future = new Promise((resolve, reject) => {
      reader.addEventListener(
        'load',
        function () {
          resolve(reader.result);
        },
        false
      );
      reader.addEventListener(
        'error',
        function (event) {
          reject(event);
        },
        false
      );

      reader.readAsDataURL(file);
    });
    return future;
  }

  openDialog(file: QuotationAttachment): void {
    this.dialogRef = this.dialog.open(InputDialogComponent, {
      hasBackdrop: true,
      data: {
        contentMsg: file.FileName,
        btnNameTwo: 'Upload',
        click: () => this.uploadFile(file),
        clickTwo: () => this.uploadFile(file),
      },
    });
  }

  async onFileSelected(event: any): Promise<void> {
    const files: FileList = event.target.files;
    if (files.length === 0) return;

    const newAttachments: QuotationAttachment[] = await Promise.all(
      Array.from(files).map(async (file) => ({
        Id: null,
        FileName: file.name,
        FilePath: '',
        CreatedBy: this.user.userName,
        AttachmentTypeId: 1,
        IsStrikethrough: false,
        IsDeleted: false,
        Data: await this.convertToBase64(file),
        IsNew: true
      }))
    );

    for (let newAttachment of newAttachments) {
      const duplicateFile = this.vendor.QuotationAttachment.find(
        (attachment) => attachment.FileName === newAttachment.FileName && !attachment.IsDeleted && !attachment.IsStrikethrough
      );
      if (duplicateFile) {
        this.openDialog(duplicateFile);
      } else {
        this.vendor.QuotationAttachment.push(newAttachment);
        this.checkVendorStatus();
        this.dataSource.data = this.vendor.QuotationAttachment.filter((attachment) => !attachment.IsDeleted);
      }
    }
    event.target.value = '';  // Reset the input
  }

  uploadFile(incomingFile: QuotationAttachment) {
    if (this.dialogRef.componentInstance.getChoice()) {
      this.renameFileAndAddToStore(incomingFile);
    } else {
      this.updateFileListWithoutDuplicates(incomingFile);
    }
    this.dialogRef.close();
  }

  renameFileAndAddToStore(fileToProcess: QuotationAttachment) {
    const originalFileName = fileToProcess.FileName.split(/\.(?=[^\.]+$)/)[0];
    const originalExtension = fileToProcess.FileName.split(/\.(?=[^\.]+$)/)[1];
    const regex = new RegExp(`${originalFileName}(?:\\(\\d+\\))?`, 'g');
    let finalList = this.vendor.QuotationAttachment.map(x => {
      const name = x.FileName.split(/\.(?=[^\.]+$)/)[0];
      const result = name.match(regex);
      return result;
    }).filter(x => !!x)
    let count: number = finalList.length;
    const newName = originalFileName +
      `(${count})` +
      '.' + originalExtension;
    fileToProcess = {
      ...fileToProcess,
      FileName: newName,
    };
    this.vendor.QuotationAttachment.push(fileToProcess);
    this.checkVendorStatus();
    this.dataSource.data = this.vendor.QuotationAttachment.filter((attachment) => !attachment.IsDeleted);
  }


  updateFileListWithoutDuplicates(fileToProcess: QuotationAttachment) {
    const indexToRemove = this.vendor.QuotationAttachment.findIndex(
      (file) => file.FileName === fileToProcess.FileName && !file.IsDeleted
    );
    if (indexToRemove !== -1) {
      this.vendor.QuotationAttachment[indexToRemove] = fileToProcess;
    } else {
      this.vendor.QuotationAttachment.push(fileToProcess);
    }
    this.checkVendorStatus();
    this.dataSource.data = this.vendor.QuotationAttachment.filter((attachment) => !attachment.IsDeleted);
  }

  removeDocument(document: QuotationAttachment): void {
    let currentAttachments = [];
    if (this.isReadOnlyRFQSummary) return;
    if (!this.vendor.QuotationAttachment) return;
    if (document.Id && !this.rfqSummaryDetails.IsRecalled) {
      document.IsDeleted = true;
    } else if (document.Id && this.rfqSummaryDetails.IsRecalled) {
      document.IsStrikethrough = true;
    } else {
      this.vendor.QuotationAttachment = this.vendor.QuotationAttachment.filter(
        (att: QuotationAttachment) => att !== document
      );
    }
    this.checkVendorStatus();
    this.dataSource.data = this.vendor.QuotationAttachment.filter((attachment) => !attachment.IsDeleted);
    this.quotationUploadChanged.emit();
  }

  async downloadVendorFile(row: QuotationAttachment) {
    try {
      let data: any = await lastValueFrom(
        this.BackendApi.downloadVendorFile(
          this.statusData.documentNo, this.statusData.rfqNum, this.vendor.VendorNumber, row.Id
        )
      );
      const extension = row.FileName.split('.')[1];
      const arrayBuffer: any = data.attachment.data;
      const int8Array = new Uint8Array(arrayBuffer);
      let blob = new Blob([int8Array], { type: `application/${extension}` });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = row.FileName;
      a.click();
      window.URL.revokeObjectURL(a.href);
    } catch (e) {
      console.log(e);
      alert('error downloading');
    }
  }

  getPurpose(id: number) {
    const reason = this.rfqAttachments.find(item => item.Id === id);
    return reason ? reason.Name : null;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

}
